/**
 * Account Merge Module
 * 
 * Функции для слияния дублирующих аккаунтов в Firebase Firestore.
 * 
 * @module accountMerge
 */

import { db } from './collections';
import {
  usersRef,
  productsRef,
  notificationsRef,
  savedSearchesRef,
  userPreferencesRef,
  addressesRef,
  feedbackRef,
  verificationsRef,
  activityLogRef,
  adPromotionsRef,
  conversationsRef,
  offersRef,
  reviewsRef,
  transactionsRef,
  followersRef,
  blockedUsersRef,
  reportsRef,
  priceHistoryRef,
} from './collections';

// Максимальное количество операций в одном batch
const MAX_BATCH_SIZE = 500;

/**
 * Обновляет все документы в коллекции, где поле равно старому userId
 * 
 * @param {string} collectionName - Имя коллекции
 * @param {string} fieldName - Имя поля для обновления
 * @param {string} oldUserId - Старый userId для замены
 * @param {string} newUserId - Новый userId
 * @returns {Promise<{success: boolean, message: string, mergedCount: number}>}
 */
async function updateCollectionField(collectionName, fieldName, oldUserId, newUserId) {
  console.log(`[AccountMerge] Updating ${collectionName}.${fieldName} from ${oldUserId} to ${newUserId}`);
  
  try {
    const collectionRef = db.collection(collectionName);
    const snapshot = await collectionRef.where(fieldName, '==', oldUserId).get();
    
    if (snapshot.empty) {
      console.log(`[AccountMerge] No documents found in ${collectionName} with ${fieldName}=${oldUserId}`);
      return { success: true, message: 'No documents to update', mergedCount: 0 };
    }
    
    let mergedCount = 0;
    let batch = db.batch();
    let batchCount = 0;
    
    for (const doc of snapshot.docs) {
      batch.update(doc.ref, { [fieldName]: newUserId });
      batchCount++;
      mergedCount++;
      
      if (batchCount >= MAX_BATCH_SIZE) {
        await batch.commit();
        console.log(`[AccountMerge] Committed batch of ${batchCount} operations for ${collectionName}`);
        batch = db.batch();
        batchCount = 0;
      }
    }
    
    // Commit remaining operations
    if (batchCount > 0) {
      await batch.commit();
      console.log(`[AccountMerge] Committed final batch of ${batchCount} operations for ${collectionName}`);
    }
    
    return {
      success: true,
      message: `Updated ${mergedCount} documents in ${collectionName}`,
      mergedCount,
    };
  } catch (error) {
    console.error(`[AccountMerge] Error updating ${collectionName}.${fieldName}:`, error);
    return {
      success: false,
      message: `Error updating ${collectionName}: ${error.message}`,
      mergedCount: 0,
    };
  }
}

/**
 * Переприсваивает все данные от deleteUserId к keepUserId
 * для коллекций с одним полем userId
 * 
 * @param {string} keepUserId - ID пользователя для сохранения
 * @param {string} deleteUserId - ID пользователя для удаления
 * @returns {Promise<{success: boolean, message: string, mergedCount: number}>}
 */
async function mergeUserData(keepUserId, deleteUserId) {
  console.log(`[AccountMerge] Merging user data from ${deleteUserId} to ${keepUserId}`);
  
  try {
    const results = [];
    let totalMergedCount = 0;
    
    // Коллекции с одним полем userId
    const singleUserCollections = [
      { name: 'products', field: 'userId' },
      { name: 'notifications', field: 'userId' },
      { name: 'savedSearches', field: 'userId' },
      { name: 'userPreferences', field: 'userId' },
      { name: 'addresses', field: 'userId' },
      { name: 'feedback', field: 'userId' },
      { name: 'verifications', field: 'userId' },
      { name: 'activityLog', field: 'userId' },
      { name: 'adPromotions', field: 'userId' },
    ];
    
    for (const { name, field } of singleUserCollections) {
      const result = await updateCollectionField(name, field, deleteUserId, keepUserId);
      results.push({ collection: name, ...result });
      totalMergedCount += result.mergedCount;
    }
    
    console.log(`[AccountMerge] User data merge complete. Total merged: ${totalMergedCount}`);
    
    return {
      success: true,
      message: `Merged ${totalMergedCount} documents across all collections`,
      mergedCount: totalMergedCount,
      details: results,
    };
  } catch (error) {
    console.error('[AccountMerge] Error merging user data:', error);
    return {
      success: false,
      message: `Error merging user data: ${error.message}`,
      mergedCount: 0,
    };
  }
}

/**
 * Обновляет коллекции с несколькими userId полями
 * 
 * @param {string} keepUserId - ID пользователя для сохранения
 * @param {string} deleteUserId - ID пользователя для удаления
 * @returns {Promise<{success: boolean, message: string, mergedCount: number}>}
 */
async function mergeMultiUserCollections(keepUserId, deleteUserId) {
  console.log(`[AccountMerge] Merging multi-user collections from ${deleteUserId} to ${keepUserId}`);
  
  try {
    let totalMergedCount = 0;
    const results = [];
    
    // Conversations: participants[], buyerId, sellerId
    console.log('[AccountMerge] Processing conversations...');
    const conversationsSnapshot = await conversationsRef()
      .where('participants', 'array-contains', deleteUserId)
      .get();
    
    let batch = db.batch();
    let batchCount = 0;
    
    for (const doc of conversationsSnapshot.docs) {
      const data = doc.data();
      const updates = {};
      
      // Update participants array
      if (data.participants && data.participants.includes(deleteUserId)) {
        const newParticipants = data.participants.map(p => 
          p === deleteUserId ? keepUserId : p
        );
        updates.participants = newParticipants;
      }
      
      // Update buyerId
      if (data.buyerId === deleteUserId) {
        updates.buyerId = keepUserId;
      }
      
      // Update sellerId
      if (data.sellerId === deleteUserId) {
        updates.sellerId = keepUserId;
      }
      
      if (Object.keys(updates).length > 0) {
        batch.update(doc.ref, updates);
        batchCount++;
        totalMergedCount++;
        
        if (batchCount >= MAX_BATCH_SIZE) {
          await batch.commit();
          batch = db.batch();
          batchCount = 0;
        }
      }
    }
    
    if (batchCount > 0) {
      await batch.commit();
    }
    results.push({ collection: 'conversations', mergedCount: conversationsSnapshot.size });
    
    // Messages (subcollection of conversations)
    console.log('[AccountMerge] Processing messages...');
    const conversationsForMessages = await conversationsRef()
      .where('participants', 'array-contains', keepUserId)
      .get();
    
    let messageCount = 0;
    for (const convDoc of conversationsForMessages.docs) {
      const messagesSnapshot = await convDoc.ref.collection('messages')
        .where('senderId', '==', deleteUserId)
        .get();
      
      if (!messagesSnapshot.empty) {
        batch = db.batch();
        batchCount = 0;
        
        for (const msgDoc of messagesSnapshot.docs) {
          batch.update(msgDoc.ref, { senderId: keepUserId });
          batchCount++;
          messageCount++;
          
          if (batchCount >= MAX_BATCH_SIZE) {
            await batch.commit();
            batch = db.batch();
            batchCount = 0;
          }
        }
        
        if (batchCount > 0) {
          await batch.commit();
        }
      }
    }
    results.push({ collection: 'messages', mergedCount: messageCount });
    totalMergedCount += messageCount;
    
    // Offers: buyerId, sellerId
    console.log('[AccountMerge] Processing offers...');
    const offersSnapshot = await offersRef()
      .where('buyerId', '==', deleteUserId)
      .get();
    
    batch = db.batch();
    batchCount = 0;
    
    for (const doc of offersSnapshot.docs) {
      batch.update(doc.ref, { buyerId: keepUserId });
      batchCount++;
      totalMergedCount++;
      
      if (batchCount >= MAX_BATCH_SIZE) {
        await batch.commit();
        batch = db.batch();
        batchCount = 0;
      }
    }
    
    if (batchCount > 0) {
      await batch.commit();
    }
    
    const offersSellerSnapshot = await offersRef()
      .where('sellerId', '==', deleteUserId)
      .get();
    
    batch = db.batch();
    batchCount = 0;
    
    for (const doc of offersSellerSnapshot.docs) {
      batch.update(doc.ref, { sellerId: keepUserId });
      batchCount++;
      totalMergedCount++;
      
      if (batchCount >= MAX_BATCH_SIZE) {
        await batch.commit();
        batch = db.batch();
        batchCount = 0;
      }
    }
    
    if (batchCount > 0) {
      await batch.commit();
    }
    results.push({ collection: 'offers', mergedCount: offersSnapshot.size + offersSellerSnapshot.size });
    
    // Reviews: sellerId, reviewerId
    console.log('[AccountMerge] Processing reviews...');
    const reviewsSellerSnapshot = await reviewsRef()
      .where('sellerId', '==', deleteUserId)
      .get();
    
    batch = db.batch();
    batchCount = 0;
    
    for (const doc of reviewsSellerSnapshot.docs) {
      batch.update(doc.ref, { sellerId: keepUserId });
      batchCount++;
      totalMergedCount++;
      
      if (batchCount >= MAX_BATCH_SIZE) {
        await batch.commit();
        batch = db.batch();
        batchCount = 0;
      }
    }
    
    if (batchCount > 0) {
      await batch.commit();
    }
    
    const reviewsReviewerSnapshot = await reviewsRef()
      .where('reviewerId', '==', deleteUserId)
      .get();
    
    batch = db.batch();
    batchCount = 0;
    
    for (const doc of reviewsReviewerSnapshot.docs) {
      batch.update(doc.ref, { reviewerId: keepUserId });
      batchCount++;
      totalMergedCount++;
      
      if (batchCount >= MAX_BATCH_SIZE) {
        await batch.commit();
        batch = db.batch();
        batchCount = 0;
      }
    }
    
    if (batchCount > 0) {
      await batch.commit();
    }
    results.push({ collection: 'reviews', mergedCount: reviewsSellerSnapshot.size + reviewsReviewerSnapshot.size });
    
    // Transactions: buyerId, sellerId
    console.log('[AccountMerge] Processing transactions...');
    const transactionsBuyerSnapshot = await transactionsRef()
      .where('buyerId', '==', deleteUserId)
      .get();
    
    batch = db.batch();
    batchCount = 0;
    
    for (const doc of transactionsBuyerSnapshot.docs) {
      batch.update(doc.ref, { buyerId: keepUserId });
      batchCount++;
      totalMergedCount++;
      
      if (batchCount >= MAX_BATCH_SIZE) {
        await batch.commit();
        batch = db.batch();
        batchCount = 0;
      }
    }
    
    if (batchCount > 0) {
      await batch.commit();
    }
    
    const transactionsSellerSnapshot = await transactionsRef()
      .where('sellerId', '==', deleteUserId)
      .get();
    
    batch = db.batch();
    batchCount = 0;
    
    for (const doc of transactionsSellerSnapshot.docs) {
      batch.update(doc.ref, { sellerId: keepUserId });
      batchCount++;
      totalMergedCount++;
      
      if (batchCount >= MAX_BATCH_SIZE) {
        await batch.commit();
        batch = db.batch();
        batchCount = 0;
      }
    }
    
    if (batchCount > 0) {
      await batch.commit();
    }
    results.push({ collection: 'transactions', mergedCount: transactionsBuyerSnapshot.size + transactionsSellerSnapshot.size });
    
    // Followers: followerId, followingId
    console.log('[AccountMerge] Processing followers...');
    const followersSnapshot = await followersRef()
      .where('followerId', '==', deleteUserId)
      .get();
    
    batch = db.batch();
    batchCount = 0;
    
    for (const doc of followersSnapshot.docs) {
      batch.update(doc.ref, { followerId: keepUserId });
      batchCount++;
      totalMergedCount++;
      
      if (batchCount >= MAX_BATCH_SIZE) {
        await batch.commit();
        batch = db.batch();
        batchCount = 0;
      }
    }
    
    if (batchCount > 0) {
      await batch.commit();
    }
    
    const followingSnapshot = await followersRef()
      .where('followingId', '==', deleteUserId)
      .get();
    
    batch = db.batch();
    batchCount = 0;
    
    for (const doc of followingSnapshot.docs) {
      batch.update(doc.ref, { followingId: keepUserId });
      batchCount++;
      totalMergedCount++;
      
      if (batchCount >= MAX_BATCH_SIZE) {
        await batch.commit();
        batch = db.batch();
        batchCount = 0;
      }
    }
    
    if (batchCount > 0) {
      await batch.commit();
    }
    results.push({ collection: 'followers', mergedCount: followersSnapshot.size + followingSnapshot.size });
    
    // BlockedUsers: blockerId, blockedId
    console.log('[AccountMerge] Processing blockedUsers...');
    const blockerSnapshot = await blockedUsersRef()
      .where('blockerId', '==', deleteUserId)
      .get();
    
    batch = db.batch();
    batchCount = 0;
    
    for (const doc of blockerSnapshot.docs) {
      batch.update(doc.ref, { blockerId: keepUserId });
      batchCount++;
      totalMergedCount++;
      
      if (batchCount >= MAX_BATCH_SIZE) {
        await batch.commit();
        batch = db.batch();
        batchCount = 0;
      }
    }
    
    if (batchCount > 0) {
      await batch.commit();
    }
    
    const blockedSnapshot = await blockedUsersRef()
      .where('blockedId', '==', deleteUserId)
      .get();
    
    batch = db.batch();
    batchCount = 0;
    
    for (const doc of blockedSnapshot.docs) {
      batch.update(doc.ref, { blockedId: keepUserId });
      batchCount++;
      totalMergedCount++;
      
      if (batchCount >= MAX_BATCH_SIZE) {
        await batch.commit();
        batch = db.batch();
        batchCount = 0;
      }
    }
    
    if (batchCount > 0) {
      await batch.commit();
    }
    results.push({ collection: 'blockedUsers', mergedCount: blockerSnapshot.size + blockedSnapshot.size });
    
    // Reports: reporterId, reportedUserId
    console.log('[AccountMerge] Processing reports...');
    const reporterSnapshot = await reportsRef()
      .where('reporterId', '==', deleteUserId)
      .get();
    
    batch = db.batch();
    batchCount = 0;
    
    for (const doc of reporterSnapshot.docs) {
      batch.update(doc.ref, { reporterId: keepUserId });
      batchCount++;
      totalMergedCount++;
      
      if (batchCount >= MAX_BATCH_SIZE) {
        await batch.commit();
        batch = db.batch();
        batchCount = 0;
      }
    }
    
    if (batchCount > 0) {
      await batch.commit();
    }
    
    const reportedSnapshot = await reportsRef()
      .where('reportedUserId', '==', deleteUserId)
      .get();
    
    batch = db.batch();
    batchCount = 0;
    
    for (const doc of reportedSnapshot.docs) {
      batch.update(doc.ref, { reportedUserId: keepUserId });
      batchCount++;
      totalMergedCount++;
      
      if (batchCount >= MAX_BATCH_SIZE) {
        await batch.commit();
        batch = db.batch();
        batchCount = 0;
      }
    }
    
    if (batchCount > 0) {
      await batch.commit();
    }
    results.push({ collection: 'reports', mergedCount: reporterSnapshot.size + reportedSnapshot.size });
    
    // PriceHistory: changedBy
    console.log('[AccountMerge] Processing priceHistory...');
    const priceHistorySnapshot = await priceHistoryRef()
      .where('changedBy', '==', deleteUserId)
      .get();
    
    batch = db.batch();
    batchCount = 0;
    
    for (const doc of priceHistorySnapshot.docs) {
      batch.update(doc.ref, { changedBy: keepUserId });
      batchCount++;
      totalMergedCount++;
      
      if (batchCount >= MAX_BATCH_SIZE) {
        await batch.commit();
        batch = db.batch();
        batchCount = 0;
      }
    }
    
    if (batchCount > 0) {
      await batch.commit();
    }
    results.push({ collection: 'priceHistory', mergedCount: priceHistorySnapshot.size });
    
    console.log(`[AccountMerge] Multi-user collections merge complete. Total merged: ${totalMergedCount}`);
    
    return {
      success: true,
      message: `Merged ${totalMergedCount} documents across multi-user collections`,
      mergedCount: totalMergedCount,
      details: results,
    };
  } catch (error) {
    console.error('[AccountMerge] Error merging multi-user collections:', error);
    return {
      success: false,
      message: `Error merging multi-user collections: ${error.message}`,
      mergedCount: 0,
    };
  }
}

/**
 * Объединяет данные документов пользователей и удаляет дубликат
 * 
 * @param {string} keepUserId - ID пользователя для сохранения
 * @param {string} deleteUserId - ID пользователя для удаления
 * @returns {Promise<{success: boolean, message: string, mergedCount: number}>}
 */
async function mergeUserDocuments(keepUserId, deleteUserId) {
  console.log(`[AccountMerge] Merging user documents from ${deleteUserId} to ${keepUserId}`);
  
  try {
    const keepDocRef = usersRef().doc(keepUserId);
    const deleteDocRef = usersRef().doc(deleteUserId);
    
    const [keepDoc, deleteDoc] = await Promise.all([
      keepDocRef.get(),
      deleteDocRef.get(),
    ]);
    
    if (!keepDoc.exists) {
      return {
        success: false,
        message: `Keep user document ${keepUserId} not found`,
        mergedCount: 0,
      };
    }
    
    if (!deleteDoc.exists) {
      return {
        success: false,
        message: `Delete user document ${deleteUserId} not found`,
        mergedCount: 0,
      };
    }
    
    const keepData = keepDoc.data();
    const deleteData = deleteDoc.data();
    
    const updates = {};
    let mergedCount = 0;
    
    // Merge savedAds arrays without duplicates
    if (deleteData.savedAds && Array.isArray(deleteData.savedAds)) {
      const currentSavedAds = keepData.savedAds || [];
      const newSavedAds = [...new Set([...currentSavedAds, ...deleteData.savedAds])];
      
      if (newSavedAds.length > currentSavedAds.length) {
        updates.savedAds = newSavedAds;
        mergedCount += newSavedAds.length - currentSavedAds.length;
      }
    }
    
    // Merge watchlist arrays without duplicates
    if (deleteData.watchlist && Array.isArray(deleteData.watchlist)) {
      const currentWatchlist = keepData.watchlist || [];
      const newWatchlist = [...new Set([...currentWatchlist, ...deleteData.watchlist])];
      
      if (newWatchlist.length > currentWatchlist.length) {
        updates.watchlist = newWatchlist;
        mergedCount += newWatchlist.length - currentWatchlist.length;
      }
    }
    
    // Update counters
    const counterFields = [
      'followersCount',
      'followingCount',
      'totalAdsPosted',
      'totalAdsSold',
      'totalActiveAds',
      'ratingSum',
      'ratingCount',
    ];
    
    for (const field of counterFields) {
      if (deleteData[field] !== undefined && typeof deleteData[field] === 'number') {
        const currentValue = keepData[field] || 0;
        const newValue = currentValue + deleteData[field];
        updates[field] = newValue;
      }
    }
    
    // Update timestamp
    updates.updatedAt = new Date().toISOString();
    
    // Apply updates
    if (Object.keys(updates).length > 0) {
      await keepDocRef.update(updates);
      console.log(`[AccountMerge] Updated user document ${keepUserId} with ${Object.keys(updates).length} fields`);
    }
    
    // Delete the duplicate document
    await deleteDocRef.delete();
    console.log(`[AccountMerge] Deleted duplicate user document ${deleteUserId}`);
    
    return {
      success: true,
      message: `Merged user documents and deleted duplicate. Updated ${Object.keys(updates).length} fields`,
      mergedCount,
      updates,
    };
  } catch (error) {
    console.error('[AccountMerge] Error merging user documents:', error);
    return {
      success: false,
      message: `Error merging user documents: ${error.message}`,
      mergedCount: 0,
    };
  }
}

/**
 * Основная функция для слияния дублирующих аккаунтов
 * 
 * @param {string} email - Email для поиска дубликатов
 * @param {string} password - Пароль (для Auth операций, если потребуется)
 * @returns {Promise<{success: boolean, message: string, mergedCount: number, details: Object}>}
 */
async function mergeDuplicateAccounts(email, password) {
  console.log(`[AccountMerge] Starting merge for email: ${email}`);
  
  try {
    // Найти все документы в коллекции users с указанным email
    const usersSnapshot = await usersRef().where('email', '==', email).get();
    
    if (usersSnapshot.empty) {
      return {
        success: false,
        message: `No accounts found with email: ${email}`,
        mergedCount: 0,
      };
    }
    
    if (usersSnapshot.size === 1) {
      return {
        success: true,
        message: 'Only one account found, no duplicates to merge',
        mergedCount: 0,
      };
    }
    
    console.log(`[AccountMerge] Found ${usersSnapshot.size} accounts with email: ${email}`);
    
    // Определить основной аккаунт для сохранения
    let keepUserId = null;
    let deleteUserId = null;
    
    const users = usersSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
    
    // Приоритет: подтверждённый (emailVerified: true) > последний созданный
    const verifiedUsers = users.filter(u => u.emailVerified === true);
    
    if (verifiedUsers.length > 0) {
      // Среди подтверждённых выбираем последний созданный
      verifiedUsers.sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bTime - aTime;
      });
      keepUserId = verifiedUsers[0].id;
    } else {
      // Нет подтверждённых, выбираем последний созданный среди всех
      users.sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bTime - aTime;
      });
      keepUserId = users[0].id;
    }
    
    // Находим ID для удаления (первый не совпадающий с keepUserId)
    deleteUserId = users.find(u => u.id !== keepUserId)?.id;
    
    if (!deleteUserId) {
      return {
        success: false,
        message: 'Could not determine which account to delete',
        mergedCount: 0,
      };
    }
    
    console.log(`[AccountMerge] Keep user: ${keepUserId}, Delete user: ${deleteUserId}`);
    
    const results = {
      userData: null,
      multiUserCollections: null,
      userDocuments: null,
    };
    
    let totalMergedCount = 0;
    
    // 1. Слияние данных с одним userId
    const userDataResult = await mergeUserData(keepUserId, deleteUserId);
    results.userData = userDataResult;
    totalMergedCount += userDataResult.mergedCount;
    
    if (!userDataResult.success) {
      return {
        success: false,
        message: `Failed to merge user data: ${userDataResult.message}`,
        mergedCount: totalMergedCount,
        details: results,
      };
    }
    
    // 2. Слияние данных с несколькими userId
    const multiUserResult = await mergeMultiUserCollections(keepUserId, deleteUserId);
    results.multiUserCollections = multiUserResult;
    totalMergedCount += multiUserResult.mergedCount;
    
    if (!multiUserResult.success) {
      return {
        success: false,
        message: `Failed to merge multi-user collections: ${multiUserResult.message}`,
        mergedCount: totalMergedCount,
        details: results,
      };
    }
    
    // 3. Слияние документов пользователей
    const userDocsResult = await mergeUserDocuments(keepUserId, deleteUserId);
    results.userDocuments = userDocsResult;
    totalMergedCount += userDocsResult.mergedCount;
    
    if (!userDocsResult.success) {
      return {
        success: false,
        message: `Failed to merge user documents: ${userDocsResult.message}`,
        mergedCount: totalMergedCount,
        details: results,
      };
    }
    
    console.log(`[AccountMerge] Merge complete. Total merged: ${totalMergedCount}`);
    
    return {
      success: true,
      message: `Successfully merged accounts. Total documents updated: ${totalMergedCount}`,
      mergedCount: totalMergedCount,
      keepUserId,
      deletedUserId: deleteUserId,
      details: results,
    };
  } catch (error) {
    console.error('[AccountMerge] Error in mergeDuplicateAccounts:', error);
    return {
      success: false,
      message: `Error merging accounts: ${error.message}`,
      mergedCount: 0,
    };
  }
}

export {
  mergeDuplicateAccounts,
  mergeUserData,
  mergeMultiUserCollections,
  mergeUserDocuments,
  updateCollectionField,
};
